import { useMemo, useState } from "react";
import { Search, ChevronRight, TrendingUp, Lightbulb } from "lucide-react";
import type { ChecklistCaseRow } from "./types";

type SortKey = "date" | "score" | "officer" | "suggestions";

type Props = {
  cases: ChecklistCaseRow[];
  onSelect: (id: string) => void;
};

export function CaseList({ cases, onSelect }: Props) {
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("date");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let rows = cases;
    if (q) {
      rows = rows.filter(
        (c) =>
          (c.officer_name || "").toLowerCase().includes(q) ||
          (c.badge_id || "").toLowerCase().includes(q) ||
          (c.case_number || "").toLowerCase().includes(q) ||
          (c.template_name || "").toLowerCase().includes(q)
      );
    }
    const dir = sortDir === "asc" ? 1 : -1;
    const sorted = [...rows].sort((a, b) => {
      switch (sortKey) {
        case "date":
          return dir * (new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
        case "score":
          return dir * ((a.score ?? -1) - (b.score ?? -1));
        case "officer":
          return dir * (a.officer_name || "").localeCompare(b.officer_name || "");
        case "suggestions":
          return dir * ((a.suggestion_count || 0) - (b.suggestion_count || 0));
        default:
          return 0;
      }
    });
    return sorted;
  }, [cases, query, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir(key === "officer" ? "asc" : "desc");
    }
  };

  const scoredCount = filtered.filter((c) => c.score != null).length;
  const avgScore = scoredCount
    ? filtered.reduce((s, c) => s + (c.score || 0), 0) / scoredCount
    : 0;

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            size={16}
          />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by officer, badge, case, or template"
            className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white"
          />
        </div>
        <div className="flex gap-2 text-xs">
          <SortButton active={sortKey === "date"} dir={sortDir} onClick={() => toggleSort("date")}>
            Date
          </SortButton>
          <SortButton active={sortKey === "score"} dir={sortDir} onClick={() => toggleSort("score")}>
            Score
          </SortButton>
          <SortButton active={sortKey === "officer"} dir={sortDir} onClick={() => toggleSort("officer")}>
            Officer
          </SortButton>
          <SortButton
            active={sortKey === "suggestions"}
            dir={sortDir}
            onClick={() => toggleSort("suggestions")}
          >
            Suggestions
          </SortButton>
        </div>
      </div>

      <div className="text-xs text-slate-500 mb-2 flex items-center gap-3">
        <span>
          Showing <span className="font-semibold text-slate-700">{filtered.length}</span>
          {query && ` of ${cases.length}`} cases
        </span>
        {scoredCount > 0 && (
          <>
            <span className="text-slate-300">·</span>
            <span>
              Avg score in view{" "}
              <span className="font-semibold text-slate-700">{avgScore.toFixed(1)}</span>
            </span>
          </>
        )}
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <div className="max-h-[560px] overflow-y-auto">
          {filtered.length === 0 && (
            <div className="px-6 py-12 text-center text-slate-500 text-sm">No cases match.</div>
          )}
          {filtered.map((c, idx) => {
            const score = c.score;
            const scoreColor =
              score == null
                ? "bg-slate-100 text-slate-500"
                : score >= 85
                  ? "bg-emerald-100 text-emerald-700"
                  : score >= 75
                    ? "bg-blue-100 text-blue-700"
                    : score >= 65
                      ? "bg-amber-100 text-amber-700"
                      : "bg-red-100 text-red-700";
            const checklistPct =
              c.total_items && c.checked_items != null
                ? (c.checked_items / c.total_items) * 100
                : 0;
            return (
              <button
                key={c.id}
                onClick={() => onSelect(c.id)}
                className={`w-full text-left flex items-center gap-4 px-4 py-3 hover:bg-blue-50/50 transition-colors border-b border-slate-100 last:border-b-0 group ${
                  idx % 2 === 0 ? "bg-white" : "bg-slate-50/40"
                }`}
              >
                <div
                  className={`shrink-0 w-12 h-12 rounded-lg flex items-center justify-center font-bold text-sm ${scoreColor}`}
                >
                  {score == null ? "n/a" : score}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-semibold text-slate-900 truncate">
                      {c.officer_name}
                    </span>
                    {c.badge_id && (
                      <span className="text-xs text-slate-400 shrink-0">#{c.badge_id}</span>
                    )}
                    {c.template_name && (
                      <span className="hidden md:inline text-[10px] uppercase font-semibold tracking-wider bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full shrink-0">
                        {c.template_name === "Supervisor Critical Incident Checklist"
                          ? "Supervisor"
                          : "Patrol"}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500">
                    <span>
                      {new Date(c.created_at).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "2-digit",
                      })}
                    </span>
                    {c.total_items != null && (
                      <span className="flex items-center gap-1">
                        <span className="font-semibold text-slate-700">
                          {c.checked_items ?? 0}/{c.total_items}
                        </span>
                        items
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Lightbulb size={11} />
                      {c.suggestion_count} suggestions
                    </span>
                    {c.implemented_count > 0 && (
                      <span className="flex items-center gap-1 text-emerald-600">
                        <TrendingUp size={11} />
                        {c.implemented_count} applied
                      </span>
                    )}
                  </div>
                  {c.total_items != null && c.total_items > 0 && (
                    <div className="mt-1.5 h-1 bg-slate-200 rounded-full overflow-hidden max-w-[260px]">
                      <div
                        className="h-full bg-blue-500 rounded-full"
                        style={{ width: `${checklistPct}%` }}
                      />
                    </div>
                  )}
                </div>
                <ChevronRight
                  size={18}
                  className="text-slate-300 group-hover:text-blue-500 shrink-0 transition-colors"
                />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function SortButton({
  active,
  dir,
  onClick,
  children,
}: {
  active: boolean;
  dir: "asc" | "desc";
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-md border transition-colors ${
        active
          ? "border-blue-500 bg-blue-50 text-blue-700 font-semibold"
          : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
      }`}
    >
      {children} {active && <span className="ml-0.5">{dir === "asc" ? "↑" : "↓"}</span>}
    </button>
  );
}
